/** Copyright (c) 2019 T. Rowe Price. All rights reserved. */

import { createSelector } from 'reselect';

import {
  DEFAULT_REQUIRED_REQUEST_STATE,
  PREFERENCES_REQUEST_STATES
} from 'components/layouts/Navigation/RequestStateConfig';
import {
  REQUEST_STATE_ERROR,
  REQUEST_STATE_RECEIVED,
  REQUEST_STATE_REQUESTED
} from 'state/requests/RequestStatusReducer';
import { isDashboardOrAccounts } from 'state/router/RouterSelectors';
import { getRouter } from 'state/router/RouterSelectors';

export const getRequestStatus = (state) => state.requestStatus;

export const getDefaultRequestStatus = createSelector(
  getRequestStatus,
  (requestStatus) => {
    const defaultRequestStatus = {};
    DEFAULT_REQUIRED_REQUEST_STATE.forEach(
      (request) => (defaultRequestStatus[request] = requestStatus[request])
    );
    return defaultRequestStatus;
  }
);

export const getLocationRequestStateKey = createSelector(
  getRouter,
  (router) => {
    const {
      location: { pathname }
    } = router;
    const cleanedPathname = pathname.replace('/', '');
    return cleanedPathname;
  }
);

export const getDefaultPreferencesRequestStatus = createSelector(
  getRequestStatus,
  (requestStatus) => {
    const defaultPreferencesRequestStatus = {};
    PREFERENCES_REQUEST_STATES.forEach(
      (request) =>
        (defaultPreferencesRequestStatus[request] = requestStatus[request])
    );
    return defaultPreferencesRequestStatus;
  }
);

export const getHasReceivedAccounts = createSelector(
  getRequestStatus,
  (requestStatus) => requestStatus.accounts === REQUEST_STATE_RECEIVED
);

export const getHasAccountsError = createSelector(
  getRequestStatus,
  (requestStatus) => requestStatus.accounts === REQUEST_STATE_ERROR
);

export const getHasReceivedAssetAllocation = createSelector(
  getRequestStatus,
  (requestStatus) => requestStatus.assetAllocation === REQUEST_STATE_RECEIVED
);

export const getHasErrorAssetAllocation = createSelector(
  getRequestStatus,
  (requestStatus) => requestStatus.assetAllocation === REQUEST_STATE_ERROR
);

export const getHasRequestedAssetAllocation = createSelector(
  getRequestStatus,
  (requestStatus) => requestStatus.assetAllocation === REQUEST_STATE_REQUESTED
);

export const getHasUpdateSubscriptions = createSelector(
  getRequestStatus,
  (requestStatus) =>
    requestStatus.updateSubscriptions !== REQUEST_STATE_REQUESTED &&
    requestStatus.updateSubscriptions !== REQUEST_STATE_ERROR
);

export const getHasErrorCustomer = createSelector(
  getRequestStatus,
  (requestStatus) => requestStatus.customer === REQUEST_STATE_ERROR
);

export const getHasReceivedCustomer = createSelector(
  getRequestStatus,
  (requestStatus) => requestStatus.customer === REQUEST_STATE_RECEIVED
);

export const getHasReceivedBrokerageData = createSelector(
  getRequestStatus,
  (requestStatus) =>
    requestStatus?.intradayPricingAutomatic === REQUEST_STATE_RECEIVED
);

export const getHasReceivedTaxonomy = createSelector(
    getRequestStatus,
    (requestStatus) => requestStatus?.cdsPortfolio === REQUEST_STATE_RECEIVED
);

export const getHasErrorTaxonomy = createSelector(
    getRequestStatus,
    (requestStatus) => requestStatus?.cdsPortfolio === REQUEST_STATE_ERROR
);

export const getHasReceivedUnrealizedData = createSelector(
    getRequestStatus,
    (requestStatus) => requestStatus?.unrealizedGainsLosses === REQUEST_STATE_RECEIVED
);

export const getHasUnrealizedDataError = createSelector(
    getRequestStatus,
    (requestStatus) => requestStatus?.unrealizedGainsLosses === REQUEST_STATE_ERROR
);

export const getHasReceivedRealizedPriorYearData = createSelector(
    getRequestStatus,
    (requestStatus) => requestStatus?.realizedGainsLossesPrior === REQUEST_STATE_RECEIVED
);

export const getHasRealizedPriorYearDataError = createSelector(
    getRequestStatus,
    (requestStatus) => requestStatus?.realizedGainsLossesPrior === REQUEST_STATE_ERROR
);

export const getHasReceivedRealizedCurrentYearData = createSelector(
    getRequestStatus,
    (requestStatus) => requestStatus?.realizedGainsLossesCurrent === REQUEST_STATE_RECEIVED
);

export const getHasRealizedCurrentYearDataError = createSelector(
    getRequestStatus,
    (requestStatus) => requestStatus?.realizedGainsLossesCurrent === REQUEST_STATE_ERROR
);

export const getIsBrokerageDataError = createSelector(
  getRequestStatus,
  (requestStatus) =>
    requestStatus?.intradayPricingAutomatic === REQUEST_STATE_ERROR
);

export const getHasReceivedTaxFiltering = createSelector(
    getRequestStatus,
    (requestStatus) => requestStatus?.customAccountGroups === REQUEST_STATE_RECEIVED
);

export const getHasReceivedNasuApplications = createSelector(
  getRequestStatus,
  (requestStatus) => requestStatus.nasuApplications === REQUEST_STATE_RECEIVED
);

export const getHasRequestedCustomer = createSelector(
  getRequestStatus,
  (requestStatus) => requestStatus.customer === REQUEST_STATE_REQUESTED
);

export const hasRequestedMonteCarloConfidenceScores = createSelector(
  getRequestStatus,
  (requestStatus) =>
    requestStatus.monteCarloConfidenceScores === REQUEST_STATE_REQUESTED
);

export const hasReceivedMonteCarloConfidenceScores = createSelector(
  getRequestStatus,
  (requestStatus) =>
    requestStatus.monteCarloConfidenceScores === REQUEST_STATE_RECEIVED
);

export const getHasRequestedExternalAccountRefresh = createSelector(
  getRequestStatus,
  (requestStatus) =>
    requestStatus.refreshExternalInvestmentBalances === REQUEST_STATE_REQUESTED
);

export const hasRequestedTaxes = createSelector(
  getRequestStatus,
  (requestStatus) => requestStatus.taxes === REQUEST_STATE_REQUESTED
);

export const hasReceivedTaxes = createSelector(
  getRequestStatus,
  (requestStatus) => requestStatus.taxes === REQUEST_STATE_RECEIVED
);

export const hasFailedRequestingTaxes = createSelector(
  getRequestStatus,
  (requestStatus) => requestStatus.taxes === REQUEST_STATE_ERROR
);

export const shouldRequestTaxes = createSelector(
  hasRequestedTaxes,
  hasReceivedTaxes,
  hasFailedRequestingTaxes,
  // Should only request if not already requested
  // i.e. not in REQUESTED, RECEIVED, or ERROR  state
  (requested, received, failed) => !requested && !received && !failed
);

export const getHasReceivedExternalAccountRefresh = createSelector(
  getRequestStatus,
  (requestStatus) =>
    requestStatus.refreshExternalInvestmentBalances === REQUEST_STATE_RECEIVED
);

export const getHasReceivedCMMVerification = createSelector(
  getRequestStatus,
  (requestStatus) => requestStatus.cmmVerification === REQUEST_STATE_RECEIVED
);

export const getHasReceivedIRTMessages = createSelector(
  getRequestStatus,
  (requestStatus) => requestStatus.irtMessages === REQUEST_STATE_RECEIVED
);

export const getHasReceivedExternalInvestmentConsent = createSelector(
  getRequestStatus,
  (requestStatus) =>
    requestStatus.externalInvestmentConsent === REQUEST_STATE_RECEIVED
);

export const getHasReceivedWorkspacePreferences = createSelector(
  getRequestStatus,
  (requestStatus) =>
    requestStatus.workspacePreferences === REQUEST_STATE_RECEIVED
);

export const getHasReceivedActivitySummaryData = createSelector(
  getRequestStatus,
  (requestStatus) => requestStatus.history === REQUEST_STATE_RECEIVED
);

export const getIsRequestingActivitySummaryData = createSelector(
  getRequestStatus,
  (requestStatus) => requestStatus.history === REQUEST_STATE_REQUESTED
);

export const getHasReceivedAccountPerformance = createSelector(
  getRequestStatus,
  (requestStatus) => requestStatus.accountPerformance === REQUEST_STATE_RECEIVED
);

export const getHasReceivedConsolidatedPerformance = createSelector(
  getRequestStatus,
  (requestStatus) =>
    requestStatus.consolidatedPerformance === REQUEST_STATE_RECEIVED
);

export const getHasReceivedProductPerformance = createSelector(
  getRequestStatus,
  (requestStatus) => requestStatus.productPerformance === REQUEST_STATE_RECEIVED
);

export const getHasReceivedAggregatedProductPerformance = createSelector(
  getRequestStatus,
  (requestStatus) =>
    requestStatus.aggregatedProductPerformance === REQUEST_STATE_RECEIVED
);

export const getHasReceivedRateOfReturn = createSelector(
  getHasReceivedAccountPerformance,
  getHasReceivedConsolidatedPerformance,
  getHasReceivedProductPerformance,
  (accountPerf, consolidatedPerf, productPerf) =>
    accountPerf && consolidatedPerf && productPerf
);

export const getIsRequestingPortfolioGrowthData = createSelector(
  getRequestStatus,
  (requestStatus) => requestStatus.accountGrowth === REQUEST_STATE_REQUESTED
);

export const getHasReceivedSecureNavigationData = createSelector(
  getHasReceivedAccounts,
  getHasReceivedCustomer,
  (receivedAccounts, receivedCustomer) => receivedAccounts && receivedCustomer
);

export const getHasReceivedMetaData = createSelector(
  getRequestStatus,
  (requestStatus) => requestStatus.metadata === REQUEST_STATE_RECEIVED
);

export const getHasReceivedPrimaryData = createSelector(
  isDashboardOrAccounts,
  getHasReceivedAccounts,
  getHasReceivedCustomer,
  getHasReceivedMetaData,
  (
    onDashboardOrAccounts,
    receivedAccounts,
    receivedCustomer,
    receivedMetaData
  ) => {
    if (onDashboardOrAccounts) {
      return receivedAccounts && receivedCustomer;
    }

    return receivedAccounts && receivedCustomer && receivedMetaData;
  }
);

export const getHasReceivedHousehold = createSelector(
  getRequestStatus,
  (requestStatus) =>
    requestStatus.householdInformation === REQUEST_STATE_RECEIVED
);
